/**
 * Niet in het origineel: de teamnaam en het einde van een spel.
 *
 * De spelers bedenken samen een teamnaam voordat ze taken spelen: één voorstel dat iedereen
 * ziet en kan aanpassen. Het staat vast zodra iedere speler met een naam akkoord is. Elke
 * aanpassing zet de akkoorden weer op nul. De spelleider kan de naam ook zelf zetten.
 *
 * Het origineel stopte nergens; de handleiding ging uit van acht rondes. Nu duurt een spel
 * hooguit `AANTAL_WEKEN` weken.
 * Daarna is het afgelopen en telt het totaal aan pallets als score in de highscores. De
 * spelleider kan een spel ook eerder afronden; dan telt de score met het aantal weken erbij.
 */
import { SpelFout } from '../contract.js';
import { pasUpdateToe } from '../mongo.js';
import { removePlayer } from './player.js';
/** Zoveel weken (rondes) duurt een spel. */
export const AANTAL_WEKEN = 12;
export const TEAMNAAM_MIN_LENGTE = 2;
/** Zo past de naam overal: op het eindscherm, de beamer en een telefoon. */
export const TEAMNAAM_MAX_LENGTE = 30;
/** De teamnaam zoals hij wordt bewaard: zonder spaties aan de randen en dubbele spaties. */
export const normaliseerTeamnaam = (naam) => naam.trim().replace(/\s+/g, ' ');
/** Wat er mis is met een teamnaam, of `undefined` als hij goed is. */
export function keurTeamnaam(naam) {
    const kaal = normaliseerTeamnaam(naam);
    if (kaal.length < TEAMNAAM_MIN_LENGTE) {
        return `Een teamnaam heeft minstens ${TEAMNAAM_MIN_LENGTE} tekens.`;
    }
    if (kaal.length > TEAMNAAM_MAX_LENGTE) {
        return `Een teamnaam heeft hooguit ${TEAMNAAM_MAX_LENGTE} tekens.`;
    }
    return undefined;
}
/**
 * Of de spelers eerst nog een teamnaam moeten kiezen: aan het begin van week 1, zolang er geen
 * naam is. Een spel dat al liep voordat de teamnaam bestond, slaat dit over.
 */
export const moetTeamnaamKiezen = (game) => !game.teamnaam &&
    !game.afgelopen &&
    game.round === 0 &&
    !game.takenVerwerkt &&
    !game.roundOver.roundOver;
/** De naam waaronder een team te zien is, ook als het er (nog) geen koos. */
export const teamnaamVan = (game) => game.teamnaam ?? `Team ${game._id}`;
/**
 * Hoeveel weken er af zijn: de lopende week telt mee zodra de pallets ervan geteld zijn
 * (`roundOver`), dus na "Alles verwerkt" en de effecten.
 */
export const gespeeldeWeken = (game) => game.roundOver.roundOver ? game.round + 1 : game.round;
/** Gooit als het spel al voorbij is; voor alles wat een nieuwe ronde in gang zet. */
export function controleerNietAfgelopen(staat) {
    if (staat.game.afgelopen)
        throw new SpelFout('Het spel is afgelopen.');
}
/** Legt de eindstand vast. Daarna start er geen ronde meer. */
export function beeindigSpel(staat, ctx) {
    const game = staat.game;
    const eindstand = {
        pallets: game.dashboard.totalCookies,
        weken: gespeeldeWeken(game),
        op: new Date(ctx.nu()).toISOString(),
    };
    pasUpdateToe(game, { $set: { afgelopen: eindstand } });
}
/** Na de pallets van een week: was dit de laatste week, dan is het spel nu afgelopen. */
export function eindigNaLaatsteWeek(staat, ctx) {
    if (!staat.game.afgelopen && gespeeldeWeken(staat.game) >= AANTAL_WEKEN) {
        beeindigSpel(staat, ctx);
    }
}
/**
 * Actie `zetTeamnaam` van de spelleider: de naam meteen vastzetten, bijvoorbeeld als een speler
 * er niet is om akkoord te geven, of om een spel van voor de teamnaam een naam te geven. Kan ook
 * na het einde, om een naam in de highscores te verbeteren: de GameRoom geeft hem dan door.
 */
export const zetTeamnaam = (staat, _ctx, naam) => {
    if (typeof naam !== 'string')
        throw new SpelFout('Vul een teamnaam in.');
    const fout = keurTeamnaam(naam);
    if (fout)
        throw new SpelFout(fout);
    pasUpdateToe(staat.game, {
        $set: { teamnaam: normaliseerTeamnaam(naam) },
        $unset: { teamnaamVoorstel: '' },
    });
};
/**
 * Niet in het origineel: plekken waar niemand met een naam is ingelogd, gaan weg zodra het spel
 * echt begint. Hun rol en kaarten gaan naar de spelers. Anders houdt een lege plek een rol vast
 * (bijvoorbeeld de telefoon van OBS) of een startkaart, zoals het incident van de menger.
 * Is nog niemand ingelogd, dan blijft alles staan.
 */
export function ruimLegePlekkenOp(staat, ctx) {
    const leeg = staat.players.players.filter((speler) => !speler.name).map((s) => s._id);
    if (leeg.length === staat.players.players.length)
        return;
    for (const id of leeg)
        removePlayer(staat, ctx, id);
}
/** De teamnaam staat vast: het voorstel weg en de lege plekken opgeruimd. */
function zetVoorstelVast(staat, ctx, naam) {
    pasUpdateToe(staat.game, {
        $set: { teamnaam: normaliseerTeamnaam(naam) },
        $unset: { teamnaamVoorstel: '' },
    });
    ruimLegePlekkenOp(staat, ctx);
}
/** De spelers die akkoord moeten geven: iedereen die met een naam is ingelogd. */
const spelersMetNaam = (staat) => staat.players.players.filter((speler) => speler.name).map((speler) => speler._id);
function controleerVoorstelMogelijk(staat) {
    if (staat.game.afgelopen)
        throw new SpelFout('Het spel is al afgelopen.');
    if (staat.game.teamnaam)
        throw new SpelFout('Jullie teamnaam staat al vast.');
}
/**
 * Actie `stelTeamnaamVoor` van een speler, bij het typen: het voorstel dat iedereen ziet. Is
 * de naam echt anders (meer dan spaties), dan beginnen de akkoorden opnieuw.
 */
export const stelTeamnaamVoor = (staat, _ctx, spelerId, naam) => {
    controleerVoorstelMogelijk(staat);
    if (typeof naam !== 'string' || typeof spelerId !== 'string') {
        throw new SpelFout('Vul een teamnaam in.');
    }
    if (normaliseerTeamnaam(naam).length > TEAMNAAM_MAX_LENGTE) {
        throw new SpelFout(`Een teamnaam heeft hooguit ${TEAMNAAM_MAX_LENGTE} tekens.`);
    }
    const vorige = staat.game.teamnaamVoorstel;
    const zelfde = vorige !== undefined && normaliseerTeamnaam(vorige.naam) === normaliseerTeamnaam(naam);
    pasUpdateToe(staat.game, {
        $set: {
            teamnaamVoorstel: { naam, door: spelerId, akkoord: zelfde ? vorige.akkoord : [] },
        },
    });
};
/**
 * Actie `keurTeamnaamGoed` van een speler: akkoord met het huidige voorstel. Is iedere speler
 * met een naam akkoord, dan staat de teamnaam vast. Geeft `true` als dat nu gebeurde.
 */
export const keurTeamnaamGoed = (staat, ctx, spelerId) => {
    controleerVoorstelMogelijk(staat);
    const voorstel = staat.game.teamnaamVoorstel;
    if (!voorstel)
        throw new SpelFout('Er is nog geen teamnaam voorgesteld.');
    const fout = keurTeamnaam(voorstel.naam);
    if (fout)
        throw new SpelFout(fout);
    const nodig = spelersMetNaam(staat);
    if (!nodig.includes(spelerId))
        throw new SpelFout('Log eerst in met je naam.');
    const akkoord = voorstel.akkoord.includes(spelerId)
        ? voorstel.akkoord
        : [...voorstel.akkoord, spelerId];
    if (nodig.every((id) => akkoord.includes(id))) {
        zetVoorstelVast(staat, ctx, voorstel.naam);
        return true;
    }
    pasUpdateToe(staat.game, { $set: { 'teamnaamVoorstel.akkoord': akkoord } });
    return false;
};
/**
 * Actie `removePlayer` van de spelleider (zie `removePlayer` in player.ts). Niet in het
 * origineel: loopt er een stemming over de teamnaam en is iedereen die overblijft al akkoord,
 * dan staat de naam nu vast. Anders bleef het team op het naamscherm staan zonder knop.
 */
export const verwijderSpeler = (staat, ctx, playerId) => {
    removePlayer(staat, ctx, playerId);
    const voorstel = staat.game.teamnaamVoorstel;
    if (!voorstel || staat.game.teamnaam || keurTeamnaam(voorstel.naam))
        return;
    const nodig = spelersMetNaam(staat);
    if (nodig.length > 0 && nodig.every((id) => voorstel.akkoord.includes(id))) {
        zetVoorstelVast(staat, ctx, voorstel.naam);
    }
};
/**
 * Actie `rondSpelAf` van de spelleider: het spel nu beëindigen, met de weken die af zijn.
 * Een week die nog loopt, telt niet mee.
 */
export const rondSpelAf = (staat, ctx) => {
    if (staat.game.afgelopen)
        throw new SpelFout('Dit spel is al afgerond.');
    if (gespeeldeWeken(staat.game) === 0) {
        throw new SpelFout('Er is nog geen week gespeeld; er is nog geen score.');
    }
    beeindigSpel(staat, ctx);
    // Wat nog openstond, telt niet meer mee: geen pop-ups of telefoontjes onder de eindstand.
    pasUpdateToe(staat.game, { $set: { effectQueue: [] } });
};
